/* eslint-disable camelcase */
import React from 'react';
import PropTypes from 'prop-types';
import { isPartnerSubsidizedAddOn } from 'src/components/PreQuestion/utils';
import { REMOTE_TRUE } from 'src/containers/AddSkuPage/constants';
import { FORM_SIZE } from 'src/components/HTKit/Forms/constants';
import Label from 'src/components/HTKit/Forms/Parts/Label';
import { ErrorMsg } from 'src/components/HTKit/Forms/Parts';
import { CalloutBox, CALLOUTBOX_THEMES } from 'Elements/CalloutBox';
import SelectField from '../HTKit/Forms/SelectField';
import { HelloTechNowMessage } from '../HTKit/Messages';
import PreQuestionLabelWithHint from './PreQuestionLabelWithHint';

const PreQuestionDropdown = ({ question, value, onChange, error, sku }) => {
  const isRemote = question.get('id') === 'remote';
  const options = question
    .get('answers')
    .map((a) => {
      let label = a.get('text');
      const warning = a.get('warning');
      const showAmount =
        a.get('adjAmountFormatted') && !isPartnerSubsidizedAddOn({ answer: a, sku });

      if (a.get('id') === REMOTE_TRUE) {
        label = label.replace('%{SAVING}%', sku.get('remoteSavingsFormatted'));
      }
      if (showAmount) {
        label += ` (+${a.get('adjAmountFormatted')})`;
      }
      const subtext = a.get('subtext');
      return { value: a.get('id'), label, warning, subtext };
    })
    .toJS();

  const hintText = question.get('hint');
  const labelProps = hintText
    ? { label: <PreQuestionLabelWithHint label={question.get('textDirect')} warning={hintText} /> }
    : { dangerouslySetLabel: question.get('textDirect') };

  /*
   * The SelectField needs to be sent an error to add the red error border to the dropdown. But we don't want the text
   * to be displayed because it is on the right side, and new Design System indicates it should be on the left. To
   * accomplish this we use <ErrorMsg> for correct placement, and send a string with a space to <SelectField>.
   */
  const legacyError = error ? ' ' : null;
  const selectedValue = value ? value.get('id') : '';

  return (
    <>
      <div className="positionRelative">
        <Label size={FORM_SIZE.medium} {...labelProps} />
        <SelectField
          placeholder="Select"
          options={options}
          value={selectedValue}
          onChange={({ value: v }) => onChange(v)}
          size="md"
          simpleValue
          clearable={false}
          searchable={false}
          error={legacyError}
        />
        <WarningCallout answers={options} comparator={selectedValue} />
        {isRemote && <HelloTechNowMessage />}
      </div>
      {error && <ErrorMsg error={error} />}
    </>
  );
};

const WarningCallout = ({ answers, comparator }) => {
  const { warning } = answers.find((answer) => answer.value === comparator) || {};

  if (warning) {
    return (
      <CalloutBox
        className="marginTop-small marginBottom-small"
        theme={CALLOUTBOX_THEMES.DEFAULT}
        text={warning}
      />
    );
  }

  return null;
};

WarningCallout.propTypes = {
  answers: PropTypes.array,
  comparator: PropTypes.number,
};

PreQuestionDropdown.propTypes = {
  question: PropTypes.object.isRequired,
  sku: PropTypes.object.isRequired,
  value: PropTypes.object,
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default PreQuestionDropdown;
